import { useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import InvoiceIcon from "@mui/icons-material/RequestQuote";
import DifferenceIcon from "@mui/icons-material/Difference";
import BonoIcon from '@mui/icons-material/CardGiftcard';
import BlockIcon from "@mui/icons-material/Block";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from '@mui/icons-material/Settings';

import Informes from "./informes/Informes";
import Facturas from "./Facturas";
import Bonos from "./Bonos";
import ServiciosSinFacturar from "./ServiciosSinFacturar";
import ServiciosNoFacturables from "./ServiciosNoFacturables";
import ConfigFacturacion from "./ConfigFacturacion";
import FacturasParciales from "./FacturasParciales";
import ConfigUtils from "../../utils/Config";
import { ROLES } from "../../common/Constantes";

const DRAWER_OPEN_WIDTH = 240;
const DRAWER_CLOSED_WIDTH = 56;

const sections = [
  { key: "informes", label: "Informes", icon: <BarChartIcon /> },
  //{ key: "servicios-sin-facturar", label: "Pendientes", icon: <ServiceIcon /> },
  { key: "facturas", label: "Facturas", icon: <InvoiceIcon /> },
  { key: "facturasParciales", label: "Facturas parciales", icon: <DifferenceIcon /> },
  { key: "bonos", label: "Bonos", icon: <BonoIcon /> },
  { key: "servicios-no-facturables", label: "No facturables", icon: <BlockIcon /> },
  { key: "configuracion", label: "Configuración", icon: <SettingsIcon /> }
];

// SECRETARIO no ve informes ni configuración (entra directo en Facturas)
const SECCIONES_SOLO_ADMIN = ["informes", "configuracion"];
const seccionVisible = (key: string, userRole?: number) =>
  userRole !== ROLES.SECRETARIO || !SECCIONES_SOLO_ADMIN.includes(key);

const FacturacionLayout = () => {
  const userRole: number | undefined = ConfigUtils.getUserRole();
  const filteredSections = sections.filter((s) => seccionVisible(s.key, userRole));

  const [activeSection, setActiveSection] = useState(filteredSections[0]?.key || "");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const renderSection = () => {
    if (!seccionVisible(activeSection, userRole)) return <Typography>Selecciona una sección</Typography>;
    switch (activeSection) {
      case "informes": return <Informes />;
      case "servicios-sin-facturar": return <ServiciosSinFacturar />;
      case "facturas": return <Facturas />;
      case "facturasParciales": return <FacturasParciales />;
      case "bonos": return <Bonos />;
      case "servicios-no-facturables": return <ServiciosNoFacturables />;
      case "configuracion": return <ConfigFacturacion />;
      default: return <Typography>Selecciona una sección</Typography>;
    }
  };

  const handleSelectSection = (key: string) => {
    setActiveSection(key);
    if (isMobile) setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: "flex", flexGrow: 1, bgcolor: "#f5f5f5", minHeight: "90.5vh" }}>
      {/* Drawer */}
      <Box
        sx={{
          width: drawerOpen ? DRAWER_OPEN_WIDTH : DRAWER_CLOSED_WIDTH,
          minWidth: drawerOpen ? DRAWER_OPEN_WIDTH : DRAWER_CLOSED_WIDTH,
          maxWidth: drawerOpen ? DRAWER_OPEN_WIDTH : DRAWER_CLOSED_WIDTH,
          bgcolor: "#042930",
          color: "white",
          display: "flex",
          flexDirection: "column",
          p: drawerOpen ? 2 : 1,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          overflowX: "hidden",
        }}
      >
        {/* Header Drawer: X o Menu */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: drawerOpen ? "space-between" : "center", mb: 3, height: 40, overflow: "hidden" }}>
          {drawerOpen ? (
            <>
              <Typography variant="h6" noWrap>Facturación</Typography>
              <Tooltip key={"close"} title={"Cerrar menú"} placement="right">
                <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: "white" }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip key={"open"} title={"Abrir menú"} placement="right">
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "white", m: "auto" }}>
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Lista de secciones */}
        <List sx={{ flexGrow: 1 }}>
          {filteredSections.map((s) => {
            const item = (
              <ListItemButton
                selected={activeSection === s.key}
                onClick={() => handleSelectSection(s.key)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  px: 1,
                  height: 48,
                  display: "flex",
                  justifyContent: drawerOpen ? "flex-start" : "center",
                  "&.Mui-selected": {
                    bgcolor: "#1976d2",
                    "&:hover": { bgcolor: "#1565c0" },
                  },
                  "&:hover": { bgcolor: "#424242" },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 0,
                    justifyContent: "center",
                  }}
                >
                  {s.icon}
                </ListItemIcon>
                
                {drawerOpen && (
                  <ListItemText
                    primary={s.label}
                    primaryTypographyProps={{
                      noWrap: true,
                      sx: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              </ListItemButton>
            );
          
            return drawerOpen ? (
              <Box key={s.key}>{item}</Box>
            ) : (
              <Tooltip key={s.key} title={s.label} placement="right">
                {item}
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* Contenido principal */}
      <Box
        sx={{
          flexGrow: 1,
          p: 4,
          overflowY: "auto",
          transition: "margin 0.3s",
        }}
      >
        {renderSection()}
      </Box>
    </Box>
  );
};

export default FacturacionLayout;
